import React, { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, Collapse, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, LinearProgress, Stack, Tab, Tabs, Typography, alpha,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  HelpOutline as HelpIcon,
  KeyboardArrowRight as NextIcon,
  KeyboardArrowLeft as PrevIcon,
} from '@mui/icons-material';
import { getSolveConfig, isStepAnswered, STEP_TYPES } from './config/workspaceTypes';
import StepPanel from './shared/StepPanel';
import ProblemCard from './shared/ProblemCard';

const SOLVE_META = [
  { key: 'split', letter: 'S', label: 'Split', color: '#4dabf7', desc: 'Break the scenario into meaningful phrases' },
  { key: 'observe', letter: 'O', label: 'Observe', color: '#69db7c', desc: 'Identify what is given and what is expected' },
  { key: 'look', letter: 'L', label: 'Look', color: '#ffd43b', desc: 'Choose the tools and approach' },
  { key: 'verify', letter: 'V', label: 'Solve & verify', color: '#ff922b', desc: 'Build the answer and prove it is right' },
  { key: 'evolve', letter: 'E', label: 'Evolve', color: '#da77f2', desc: 'Optimise, document, and make it reusable' },
];

const RESPONSE_GUIDES = {
  split: {
    title: 'Split with IPOPS',
    purpose:
      'Break the scenario into its five key parts before solving it.\n\n' +
      '• Input – What are you starting with? (data, files, tables, APIs, requirements)\n' +
      '• Problem – What issue or pain point needs solving?\n' +
      '• Operation – What work, process, or solution will be performed?\n' +
      '• Product – What will be produced or delivered?\n' +
      '• Success – How do you know it worked?\n\n' +
      'Tip: One IPOPS category may contain several related phrases from the scenario. Don’t force every sentence into exactly one category.\n\n' +
      'IPOPS becomes the foundation for every remaining SOLVE step.',
    pattern: [
      'Input',
      '- ...',
      '',
      'Problem',
      '- ...',
      '',
      'Operation',
      '- ...',
      '',
      'Product',
      '- ...',
      '',
      'Success',
      '- ...',
    ],

    example: [
      'Example 1 – SQL Data Warehouse',
      '',
      'Scenario:',
      "RetailEdge's analytics team needs a weekly revenue report by product category and store region. Five analysts each write their own joins against the same raw tables and get five different totals. A shared dimensional model—one fact table, clear dimension tables, and standard window-function queries—will make every analyst's results agree.",
      '',
      'Input',
      '- raw sales tables',
      '',
      'Problem',
      '- analysts write different joins',
      '- same data produces different totals',
      '',
      'Operation',
      '- build a shared dimensional model',
      '- create one fact table',
      '- create dimension tables',
      '- use standard joins and window functions',
      '',
      'Product',
      '- weekly revenue report',
      '- grouped by product category and store region',
      '',
      'Success',
      '- every analyst produces the same totals',
      '',
      '────────────────────────────',
      '',
      'Example 2 – Python ETL Pipeline',
      '',
      'Scenario:',
      'Every night, 50 regional stores upload CSV files to Azure Blob Storage. The files often contain duplicate rows, missing values, and inconsistent date formats, causing failed Power BI refreshes the next morning. Build an automated ETL pipeline that validates, cleans, deduplicates, and loads the data into Azure SQL Database while logging every processing step. The business expects the pipeline to finish in under 15 minutes with complete audit logs and reliable daily reporting.',
      '',
      'Input',
      '- nightly CSV files',
      '- Azure Blob Storage',
      '- store sales data',
      '',
      'Problem',
      '- duplicate rows',
      '- missing values',
      '- inconsistent date formats',
      '- Power BI refresh failures',
      '',
      'Operation',
      '- automate ETL pipeline',
      '- validate incoming files',
      '- clean and deduplicate data',
      '- load into Azure SQL Database',
      '- log every processing step',
      '',
      'Product',
      '- clean validated dataset',
      '- loaded into Azure SQL Database',
      '- ready for Power BI reporting',
      '',
      'Success',
      '- pipeline finishes in under 15 minutes',
      '- audit logs are available',
      '- daily reports refresh successfully',
      '',
      '────────────────────────────',
      '',
      'Example 3 – Machine Learning',
      '',
      'Scenario:',
      'A telecom company wants to reduce customer churn. Customer information is stored across billing, CRM, and usage systems with inconsistent identifiers and missing records. Build a machine learning pipeline that combines the data, engineers useful features, trains a churn prediction model, and deploys predictions to a dashboard so the retention team can contact at-risk customers. The solution should achieve at least 90% recall while generating predictions every morning.',
      '',
      'Input',
      '- billing data',
      '- CRM data',
      '- usage data',
      '',
      'Problem',
      '- fragmented customer data',
      '- inconsistent identifiers',
      '- missing records',
      '- no reliable churn prediction',
      '',
      'Operation',
      '- combine datasets',
      '- engineer features',
      '- train machine learning model',
      '- deploy predictions',
      '',
      'Product',
      '- churn prediction model',
      '- dashboard with daily customer risk scores',
      '',
      'Success',
      '- at least 90% recall',
      '- predictions generated every morning',
    ],
  },
  observe: {
    title: 'Observe with GAMES',

    purpose:
      'Use the IPOPS you identified to examine the scenario more deeply.\n\n' +
      '• Given – Expand the Input. What facts, data, or resources already exist?\n' +
      '• Ask – Expand the Problem and Product. What exactly is being requested?\n' +
      '• Missing – What important information is not provided?\n' +
      '• Edge Risks – What could cause your Operation to fail?\n' +
      '• Samples – What examples or evidence would help understand or verify the Product?\n\n' +
      'Typical mapping:\n' +
      'Input → Given\n' +
      'Problem → Ask, Missing\n' +
      'Operation → Edge Risks\n' +
      'Product → Samples\n' +
      'Success → Validation criteria later in PROVE\n\n' +
      'Think of GAMES as asking better questions about your IPOPS.',
    pattern: [
      'Given',
      '- ...',
      '',
      'Ask',
      '- ...',
      '',
      'Missing',
      '- ...',
      '',
      'Edge Risks',
      '- ...',
      '',
      'Samples',
      '- ...',
    ],

    example: [
      'Example – ETL Pipeline',
      '',
      'Given',
      '- CSV files',
      '- Azure Blob Storage',
      '- Azure SQL',
      '',
      'Ask',
      '- automate the ETL pipeline',
      '',
      'Missing',
      '- expected CSV schema',
      '- handling invalid rows',
      '',
      'Edge Risks',
      '- duplicate files',
      '- corrupt CSVs',
      '- missing columns',
      '',
      'Samples',
      '- one valid file',
      '- one invalid file',
      '- row counts before and after cleaning',
      '',
      '────────────────────────────',
      '',
      'Example – SQL Reporting',
      '',
      'Given',
      '- sales tables',
      '- product tables',
      '',
      'Ask',
      '- weekly revenue report',
      '',
      'Missing',
      '- fiscal calendar',
      '- revenue definition',
      '',
      'Edge Risks',
      '- duplicate sales',
      '- missing products',
      '',
      'Samples',
      '- sample joins',
      '- expected report totals',
    ],
  },
  look: {
    title: 'Look with TOOLS',

    purpose:
      'Use what you discovered in IPOPS and GAMES to choose the simplest technical solution.\n\n' +
      '• Tool – Which technology, feature, or technique will you use?\n' +
      '• Outcome – Which IPOPS Problem or Product does it help solve?\n' +
      '• Other Option – What reasonable alternative exists?\n' +
      '• Limits – Which risks from GAMES should you consider?\n' +
      '• Smallest Choice – What is the simplest solution that satisfies the Product and Success?\n\n' +
      'Typical mapping:\n' +
      'Input → Tool\n' +
      'Problem → Outcome\n' +
      'Operation → Tool + Other Option + Limits\n' +
      'Product → Smallest Choice\n\n' +
      'Choose tools because they solve the problem—not because they are popular.',
    pattern: [
      'Tool',
      '- ...',
      '',
      'Outcome',
      '- ...',
      '',
      'Other Option',
      '- ...',
      '',
      'Limits',
      '- ...',
      '',
      'Smallest Choice',
      '- ...',
    ],

    example: [
      'Example – ETL',
      '',
      'Tool',
      '- Pandas',
      '',
      'Outcome',
      '- clean CSV data',
      '',
      'Other Option',
      '- Spark',
      '',
      'Limits',
      "- Pandas doesn't scale well to huge datasets",
    '',
      'Smallest Choice',
      '- Pandas is sufficient',
      '',
      '────────────────────────────',
      '',
      'Example – SQL',
      '',
      'Tool',
      '- Window Functions',
      '',
      'Outcome',
      '- ranking and running totals',
      '',
      'Other Option',
      '- correlated subqueries',
      '',
      'Limits',
      '- harder to understand initially',
      '',
      'Smallest Choice',
      '- ROW_NUMBER() and SUM() OVER()',
    ],
  },
  verify: {
    title: 'Verify with PROVE',

    purpose:
      'Use the Success criteria from IPOPS and the risks from GAMES to prove your solution works.\n\n' +
      '• Produce – Show the working solution.\n' +
      '• Reconcile – Compare with expected results.\n' +
      '• Observe – Test the edge cases identified in GAMES.\n' +
      '• Validate – Confirm all requirements are satisfied.\n' +
      '• Evidence – Provide proof that your solution works.\n\n' +
      'Typical mapping:\n' +
      'Success → Validate\n' +
      'Edge Risks → Observe\n' +
      'Product → Produce\n\n' +
      "Don't assume your solution is correct—prove it.",
    pattern: [
      'Produce',
      '- ...',
      '',
      'Reconcile',
      '- ...',
      '',
      'Observe',
      '- ...',
      '',
      'Validate',
      '- ...',
      '',
      'Evidence',
      '- ...',
    ],

    example: [
      'Example – SQL',
      '',
      'Produce',
      '- weekly revenue query',
      '',
      'Reconcile',
      '- compare totals with finance report',
      '',
      'Observe',
      '- stores with zero sales',
      '',
      'Validate',
      '- totals match expected output',
      '',
      'Evidence',
      '- SQL results and screenshots',
      '',
      '────────────────────────────',
      '',
      'Example – ETL',
      '',
      'Produce',
      '- cleaned dataset',
      '',
      'Reconcile',
      '- input vs output row counts',
      '',
      'Observe',
      '- duplicates and invalid rows',
      '',
      'Validate',
      '- Power BI refresh succeeds',
      '',
      'Evidence',
      '- pipeline logs',
    ],
  },
  evolve: {
    title: 'Evolve with SCALE',
    purpose:
      'Once your solution is proven, improve it so it is easier to maintain, reuse, and operate.\n\n' +
      '• Simplify – Reduce unnecessary complexity.\n' +
      '• Control – Make behaviour configurable.\n' +
      '• Automate – Remove manual work.\n' +
      '• Log – Record useful operational information.\n' +
      '• Explain – Document how the solution works.\n\n' +
      "SCALE builds on everything you've already completed.The solution already works—now make it production- ready.",
    pattern: [
      'Simplify',
      '- ...',
      '',
      'Control',
      '- ...',
      '',
      'Automate',
      '- ...',
      '',
      'Log',
      '- ...',
      '',
      'Explain',
      '- ...',
    ],

    example: [
      'Example – ETL',
      '',
      'Simplify',
      '- split into reusable functions',
      '',
      'Control',
      '- configurable input folder',
      '',
      'Automate',
      '- scheduled nightly execution',
      '',
      'Log',
      '- processing statistics',
      '',
      'Explain',
      '- README and architecture diagram',
      '',
      '────────────────────────────',
      '',
      'Example – SQL',
      '',
      'Simplify',
      '- reusable CTEs',
      '',
      'Control',
      '- parameterised report dates',
      '',
      'Automate',
      '- scheduled report generation',
      '',
      'Log',
      '- execution time',
      '',
      'Explain',
      '- document star schema',
    ],
  },
};

const SOLVEWorkspace = ({
  item,
  responses,
  onResponseChange,
  disabled,
  onComplete,
  saveStatus,
}) => {
  const taskType = item?.context?.task_type || 'generic';
  const solveConfig = useMemo(() => getSolveConfig(taskType), [taskType]);
  const [stepIndex, setStepIndex] = useState(0);
  const [showGuidance, setShowGuidance] = useState({});
  const [guideOpen, setGuideOpen] = useState(false);

  const currentMeta = SOLVE_META[stepIndex];
  const currentResponse = responses[currentMeta?.key] || {};
  const itemContext = item?.context || {};

  const guidance = item?.guidance?.solve?.[currentMeta?.key] || '';
  const stepHint = item?.stepOverrides?.solve?.[currentMeta?.key]?.hint || '';

  const configForStep = (key) => ({
    ...(solveConfig[key] || {}),
    ...(item?.stepOverrides?.solve?.[key] || {}),
  });

  const currentConfig = configForStep(currentMeta?.key);
  const currentGuide = RESPONSE_GUIDES[currentMeta?.key];

  const isReadyForStep = (meta) => {
    const config = configForStep(meta.key);
    const response = responses[meta.key];
    if (config?.type === STEP_TYPES.STRUCTURED && config.prompts?.length) {
      const answers = response?.answers || {};
      return config.prompts.every((_, index) => String(answers[index] || '').trim().length > 0);
    }
    return isStepAnswered(response);
  };

  const answeredCount = SOLVE_META.filter((m) => isReadyForStep(m)).length;
  const allAnswered = answeredCount === SOLVE_META.length;
  const currentStepReady = isReadyForStep(currentMeta);
  const progressValue = (answeredCount / SOLVE_META.length) * 100;
  const getValidationHint = () => {
    if (currentStepReady) return 'Ready. Use Next when you are happy with this tab.';
    if (currentConfig?.type === STEP_TYPES.STRUCTURED && currentConfig.prompts?.length) {
      const answers = currentResponse?.answers || {};
      const missing = currentConfig.prompts.filter((_, index) => String(answers[index] || '').trim().length === 0).length;
      return `Answer ${missing} more prompt${missing === 1 ? '' : 's'} to unlock Next.`;
    }
    return 'Add an answer or selection in this tab to unlock Next.';
  };

  const handleChange = (value) => {
    onResponseChange(currentMeta.key, value);
  };

  if (!currentConfig) {
    return (
      <Alert severity="error">
        No SOLVE config found for task type "{taskType}". Check workspaceTypes.js.
      </Alert>
    );
  }

  return (
    <Stack spacing={0}>
      {/* SOLVE step tabs */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          overflowX: 'auto',
          px: { xs: 1, md: 2 },
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.025),
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          sx={{ pt: 1.25, pb: 0.75, px: 0.5 }}
        >
          <Box sx={{ minWidth: 220, flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
              <Typography variant="caption" fontWeight={800} color="text.secondary">
                SOLVE progress
              </Typography>
              <Chip
                label={`${answeredCount}/${SOLVE_META.length} tabs complete`}
                size="small"
                color={allAnswered ? 'success' : 'primary'}
                variant={allAnswered ? 'filled' : 'outlined'}
                sx={{ height: 20, fontSize: '0.68rem' }}
              />
            </Stack>
            <LinearProgress
              variant="determinate"
              value={progressValue}
              sx={{ height: 6, borderRadius: 999, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12) }}
            />
          </Box>
          <Chip
            label="Manual tabs"
            size="small"
            variant="outlined"
            sx={{ height: 24, fontSize: '0.7rem', alignSelf: { xs: 'flex-start', sm: 'center' } }}
          />
        </Stack>

        <Tabs
          value={stepIndex}
          onChange={(_, v) => setStepIndex(v)}
          variant="scrollable"
          scrollButtons="auto"
          TabIndicatorProps={{ style: { backgroundColor: currentMeta?.color } }}
        >
          {SOLVE_META.map((meta, i) => {
            const answered = isReadyForStep(meta);
            const active = stepIndex === i;
            return (
              <Tab
                key={meta.key}
                value={i}
                label={
                  <Stack direction="row" spacing={0.9} alignItems="center" sx={{ minWidth: { xs: 96, sm: 118 } }}>
                    <Box
                      sx={{
                        width: 28, height: 28, borderRadius: '50%',
                        display: 'grid', placeItems: 'center',
                        bgcolor: active ? meta.color : answered ? alpha(meta.color, 0.28) : alpha(meta.color, 0.11),
                        color: active ? '#000' : answered ? 'text.primary' : 'text.secondary',
                        fontWeight: 800, fontSize: '0.78rem',
                        transition: 'all 0.15s',
                        flexShrink: 0,
                        border: `1px solid ${alpha(meta.color, active ? 0.9 : 0.35)}`,
                      }}
                    >
                      {answered ? <CheckIcon sx={{ fontSize: 14 }} /> : meta.letter}
                    </Box>
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography component="span" display="block" fontSize="0.78rem" fontWeight={800}>
                        {meta.label}
                      </Typography>
                      <Typography component="span" display="block" fontSize="0.62rem" color="text.secondary">
                        {answered ? 'Done' : `Step ${i + 1}`}
                      </Typography>
                    </Box>
                  </Stack>
                }
                sx={{
                  minHeight: 58,
                  py: 0.75,
                  px: 1.25,
                  opacity: 1,
                  borderRadius: 1,
                  mr: 0.5,
                  bgcolor: active ? alpha(meta.color, 0.1) : 'transparent',
                }}
              />
            );
          })}
        </Tabs>
      </Box>

      {/* Step header */}
      <Box sx={{ px: { xs: 2, md: 3 }, pt: 2.5, pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ fontWeight: 700, letterSpacing: 1.5 }}
            >
              Step {stepIndex + 1} of 5 — {currentMeta.label}
            </Typography>
            <Typography variant="h6" fontWeight={800} mt={0.25}>
              {item?.stepOverrides?.solve?.[currentMeta.key]?.title || currentMeta.label}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {item?.stepOverrides?.solve?.[currentMeta.key]?.instruction || currentMeta.desc}
            </Typography>
          </Box>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'flex-end', sm: 'center' }}
            sx={{ mt: 0.5, flexShrink: 0 }}
          >
            <Button
              size="small"
              variant="outlined"
              startIcon={<HelpIcon />}
              onClick={() => setGuideOpen(true)}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Response guide
            </Button>
            <Chip
              label={currentStepReady ? 'This tab is complete' : 'Finish this tab to continue'}
              size="small"
              color={currentStepReady ? 'success' : 'default'}
              variant={currentStepReady ? 'filled' : 'outlined'}
            />
          </Stack>
        </Stack>
        <Alert severity={currentStepReady ? 'success' : 'info'} icon={false} sx={{ mt: 1.5, py: 0.5 }}>
          <Typography variant="caption" fontWeight={700}>{getValidationHint()}</Typography>
        </Alert>

        {/* Task problem card */}
        {item?.prompt && (
          <Box mt={2}>
            <ProblemCard item={item} accentColor={currentMeta.color} defaultExpanded={stepIndex === 0} />
          </Box>
        )}
      </Box>

      {/* Step workspace */}
      <Box sx={{ px: { xs: 2, md: 3 }, py: 2 }}>
        {stepHint && (
          <Alert severity="info" icon={false} sx={{ mb: 2 }}>
            <Typography variant="body2">{stepHint}</Typography>
          </Alert>
        )}
        <StepPanel
          stepConfig={currentConfig}
          itemContext={itemContext}
          response={currentResponse}
          onChange={handleChange}
          disabled={disabled}
        />
      </Box>

      {/* Guidance toggle */}
      {guidance && (
        <Box sx={{ px: { xs: 2, md: 3 }, pb: 1 }}>
          <Button
            size="small"
            variant="text"
            disabled={!currentStepReady}
            onClick={() =>
              setShowGuidance((prev) => ({
                ...prev,
                [currentMeta.key]: !prev[currentMeta.key],
              }))
            }
          >
            {showGuidance[currentMeta.key] ? 'Hide expert approach' : 'Compare with expert approach'}
          </Button>
          <Collapse in={Boolean(showGuidance[currentMeta.key])}>
            <Alert severity="success" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
              {guidance}
            </Alert>
          </Collapse>
        </Box>
      )}

      <Dialog
        open={guideOpen}
        onClose={() => setGuideOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 800 }}>
            SOLVE response guide
          </Typography>
          <Typography variant="h6" fontWeight={900}>
            {currentGuide?.title || 'How to write a clear answer'}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="info" icon={false}>
              <Typography variant="body2">
                Keep your response short and specific. Bullets are fine. Sentences are fine.
                The goal is clarity, not a long essay. Use backticks for inline code,
                for example `files = sorted(pathlib.Path(folder).glob("*.csv"))`.
              </Typography>
            </Alert>

            <Alert severity="success" icon={false}>
              <Typography variant="body2">
                SOLVE is a reasoning pipeline:
                <br />
                IPOPS → GAMES → TOOLS → PROVE → SCALE
                <br />
                Each step builds on the previous one.
              </Typography>
            </Alert>

            {currentGuide?.purpose && (
              <Box>
                <Typography variant="subtitle2" fontWeight={900} gutterBottom>
                  Purpose
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {currentGuide.purpose}
                </Typography>
              </Box>
            )}

            {currentConfig?.prompts?.length > 0 && (
              <Box>
                <Typography variant="subtitle2" fontWeight={900} gutterBottom>
                  Answer these prompts directly
                </Typography>
                <Stack component="ol" spacing={0.75} sx={{ pl: 2.5, m: 0 }}>
                  {currentConfig.prompts.map((prompt) => (
                    <Typography component="li" variant="body2" key={prompt}>
                      {prompt}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            )}

            {currentGuide?.pattern?.length > 0 && (
              <Box>
                <Typography variant="subtitle2" fontWeight={900} gutterBottom>
                  Simple answer pattern
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                    fontSize: '0.8rem',
                    lineHeight: 1.7,
                  }}
                >
                  {currentGuide.pattern.join('\n')}
                </Box>
              </Box>
            )}

            {currentGuide?.example?.length > 0 && (
              <Box>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle2" fontWeight={900} gutterBottom>
                  Example style
                </Typography>
                <Stack spacing={0.75}>
                  {currentGuide.example.map((line) => (
                    <Typography variant="body2" key={line}>
                      {line}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGuideOpen(false)} variant="contained">
            Got it
          </Button>
        </DialogActions>
      </Dialog>

      {/* Step nav */}
      <Box
        sx={{
          px: { xs: 2, md: 3 }, py: 2,
          borderTop: 1, borderColor: 'divider',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          gap: 1,
        }}
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={<PrevIcon />}
          disabled={stepIndex === 0}
          onClick={() => setStepIndex((i) => i - 1)}
        >
          Previous
        </Button>

        <Typography variant="caption" color="text.secondary">
          {saveStatus === 'saving' && 'Saving…'}
          {saveStatus === 'saved' && 'Saved ✓'}
          {saveStatus === 'error' && 'Save failed'}
        </Typography>

        {stepIndex < SOLVE_META.length - 1 ? (
          <Button
            variant="contained"
            size="small"
            endIcon={<NextIcon />}
            disabled={!currentStepReady}
            onClick={() => setStepIndex((i) => i + 1)}
          >
            Next: {SOLVE_META[stepIndex + 1].label}
          </Button>
        ) : (
          <Button
            variant="contained"
            color="success"
            size="small"
            disabled={!allAnswered}
            onClick={onComplete}
          >
            Complete SOLVE ✓
          </Button>
        )}
      </Box>
    </Stack>
  );
};

export default SOLVEWorkspace;
